export type BlogCategory =
  | "AI Search"
  | "Technical SEO"
  | "Local SEO"
  | "Content Strategy"
  | "Analytics"
  | "Case Teardown";

export interface BlogSection {
  h: string;
  p: string[];
}

export interface BlogImage {
  src: string;
  alt: string;
  caption?: string;
  credit?: string;
}

export interface BlogPost {
  slug: string;
  title: string;
  dek: string;
  category: BlogCategory;
  readingMinutes: number;
  publishedISO: string;
  updatedISO?: string;
  authorSlug: "umair-mansha";
  tags: string[];
  toc: string[];
  intro: string[];
  heroImage: BlogImage;
  bodyImage: BlogImage;
  sections: BlogSection[];
  keyTakeaways: string[];
  related?: string[];
}

const UMAIR = "umair-mansha" as const;

const UNSPLASH = (id: string, w = 1600) =>
  `https://images.unsplash.com/photo-${id}?w=${w}&q=80&auto=format&fit=crop`;

export const BLOG_CATEGORIES: BlogCategory[] = [
  "AI Search",
  "Technical SEO",
  "Local SEO",
  "Content Strategy",
  "Analytics",
  "Case Teardown",
];

export const BLOG_POSTS: BlogPost[] = [
  {
    slug: "how-to-rank-in-ai-overviews",
    title: "How to rank in AI Overviews: the 2026 playbook",
    dek: "Semrush shows 720 monthly searches for this exact question and rising. Here is the exact citation-earning playbook we ship in engagements — the entity graph, the first-100-words rewrite, the schema stack, and the tracking cadence.",
    category: "AI Search",
    readingMinutes: 13,
    publishedISO: "2026-07-27",
    authorSlug: UMAIR,
    tags: ["AI Overviews", "AEO", "GEO", "citations", "SGE"],
    toc: ["Overview", "Three Signals", "Rewrites", "Entity Graph", "Schema", "Tracking", "30-day Sprint"],
    intro: ["Ranking is the wrong frame.", "Earn citations instead."],
    heroImage: { src: UNSPLASH("1573164713988-8665fc963095"), alt: "Marketer" },
    bodyImage: { src: UNSPLASH("1504868584819-f8e8b4b6d7e3"), alt: "SEO consultants" },
    sections: [
      { h: "Why ranking is wrong", p: ["Overview is the new real estate.", "Moves diverge from classical SEO."] },
      { h: "Three signals", p: ["Answer clarity.", "Entity strength.", "Topical cluster."] }
    ],
    keyTakeaways: ["Citations over rankings.", "Entity graph matters."],
    related: ["ai-search-optimization-guide"],
    updatedISO: "2026-07-28"
  },
  {
    slug: "ai-search-optimization-guide",
    title: "AI search optimization: the complete 2026 guide",
    dek: "AEO, GEO, LLM-SEO — the labels multiplied faster than the practice. Here is the engineering reality behind the acronyms.",
    category: "AI Search",
    readingMinutes: 15,
    publishedISO: "2026-07-27",
    updatedISO: "2026-07-28",
    authorSlug: UMAIR,
    tags: ["AI search", "AEO", "GEO", "LLM SEO"],
    toc: ["Labels", "Surfaces", "Foundation", "Structure", "Off-page", "Measurement", "Budget"],
    intro: ["Shift from blue links to answers."],
    heroImage: { src: UNSPLASH("1620712943543-bcc4688e7485"), alt: "AI search" },
    bodyImage: { src: UNSPLASH("1553877522-43269d4ea984"), alt: "Strategist" },
    sections: [
      { h: "AEO vs GEO", p: ["Distinction is measurement.", "Underlying moves overlap."] }
    ],
    keyTakeaways: ["Overlap is 80%.", "Technical foundation is key."],
    related: ["how-to-rank-in-ai-overviews"]
  },
  {
    slug: "dallas-seo-case-study",
    title: "Dallas SEO: How we increased organic clicks by 214% in 6 months",
    dek: "A deep dive into the 'Dallas Growth engine' we built for a multi-location service brand.",
    category: "Case Teardown",
    readingMinutes: 12,
    publishedISO: "2026-08-01",
    updatedISO: "2026-08-02",
    authorSlug: UMAIR,
    tags: ["Dallas SEO", "Local SEO", "Case Study"],
    toc: ["Start", "Engine", "Architecture", "GBP", "Results"],
    intro: ["Invisible in North Dallas.", "Deploying the growth engine."],
    heroImage: { src: UNSPLASH("1460925895917-afdab827c52f"), alt: "Data growth" },
    bodyImage: { src: UNSPLASH("1557804506-669a67965ba0"), alt: "Dallas meeting" },
    sections: [
      { h: "Invisible start", p: ["Single contact page failed.", "Crawl budget wasted."] }
    ],
    keyTakeaways: ["City-service pages rank.", "Local entity signals move the needle."],
    related: ["how-to-rank-in-ai-overviews"]
  },
  {
    slug: "local-seo-checklist-2026",
    title: "The 2026 Local SEO checklist: How we fix CTR and indexing issues",
    dek: "A prioritized framework for closing content gaps and reclaiming clicks based on our internal audit of over 150 local service markets.",
    category: "Local SEO",
    readingMinutes: 14,
    publishedISO: "2026-08-06",
    updatedISO: "2026-08-07",
    authorSlug: UMAIR,
    tags: ["Local SEO", "CTR", "Indexing", "Checklist"],
    toc: ["Checklist", "CTR optimization", "Indexing fixes", "Content gaps", "Monitoring"],
    intro: ["Most businesses lose 30% of their clicks to poor meta data and crawl errors."],
    heroImage: { src: UNSPLASH("1551288049-bbdac8626ad1"), alt: "Analytics dashboard" },
    bodyImage: { src: UNSPLASH("1460925895917-afdab827c52f"), alt: "Growth data" },
    sections: [
      { h: "The CTR framework", p: ["Titles must answer intent immediately.", "Meta descriptions are sales copy, not summaries."] },
      { h: "Indexing & crawl depth", p: ["Flat architecture wins.", "Internal links from high-authority city pages are the primary signal."] }
    ],
    keyTakeaways: ["Fix high-impression, low-CTR pages first.", "Use city-level hubs to boost discovery."],
    related: ["dallas-seo-case-study"]
  },
  {
    slug: "technical-seo-indexing-monitoring",
    title: "Technical SEO monitoring: Verifying crawl and index status at scale",
    dek: "Why wait for GSC updates? How we set up real-time indexing alerts and crawl monitoring for multi-location brands.",
    category: "Technical SEO",
    readingMinutes: 11,
    publishedISO: "2026-08-06",
    updatedISO: "2026-08-07",
    authorSlug: UMAIR,
    tags: ["Technical SEO", "Indexing", "Monitoring", "Crawl"],
    toc: ["Monitoring", "Alerts", "Verification", "Fixes"],
    intro: ["Crawl budget is finite. Don't waste it on junk."],
    heroImage: { src: UNSPLASH("1558494949-ef01017911c4"), alt: "Server data" },
    bodyImage: { src: UNSPLASH("1518770660439-4636190af475"), alt: "Technical hardware" },
    sections: [
      { h: "Real-time alerts", p: ["Identify 404s and 301s before they drop from the index.", "Monitor server response times to protect INP."] }
    ],
    keyTakeaways: ["Automation protects rankings.", "Speed is a primary technical signal."],
    related: ["local-seo-checklist-2026"]
  }
];

export function listBlogPosts() {
  return BLOG_POSTS;
}

export function getBlogPost(slug: string) {
  return BLOG_POSTS.find((p) => p.slug === slug);
}
